/**
 * mira-telemetry — best-effort browser diagnostics for the Mira widget.
 *
 * Why module-level?
 *   The console capture has to be running BEFORE the user opens Mira so
 *   we catch errors that occurred earlier in the session (e.g. a stack
 *   trace logged on page load that's exactly what they want to report).
 *   We install the patch on first import. App.tsx imports this module
 *   at the top of its file so the side effect fires on app boot.
 *
 * Privacy:
 *   We collect only data already visible in the browser's DevTools —
 *   userAgent, viewport, current URL pathname/search (NOT hash), and
 *   recent console messages. No request bodies, no cookies, no
 *   localStorage contents.
 *
 * Ring buffer:
 *   Console messages live in a fixed-size FIFO so a chatty page can't
 *   exhaust memory. 100 entries × ~500 char each ≈ 50 KB ceiling.
 */

export type ConsoleLevel = "log" | "info" | "warn" | "error" | "debug";

export interface ConsoleEntry {
  level: ConsoleLevel;
  message: string;
  at: string; // ISO 8601
}

export interface Telemetry {
  user_agent: string;
  platform: string;
  language: string;
  languages: string[];
  timezone: string;
  online: boolean;
  cookie_enabled: boolean;
  hardware_concurrency: number | null;
  device_memory: number | null;
  viewport: { width: number; height: number; pixel_ratio: number };
  screen: { width: number; height: number };
  page: {
    url: string;       // full URL (no hash for privacy / fragment-state apps)
    pathname: string;
    search: string;
    referrer: string;
  };
  // Best-effort connection info — undefined on browsers that lack the API.
  connection?: {
    effective_type?: string;
    downlink?: number;
    rtt?: number;
    save_data?: boolean;
  };
  collected_at: string; // ISO 8601
}

// ──────────────────────────────────────────────────────────────────────
// Console ring buffer
// ──────────────────────────────────────────────────────────────────────

const MAX_ENTRIES = 100;
const MAX_MESSAGE_CHARS = 1000;

const _buffer: ConsoleEntry[] = [];

function _safeStringify(arg: unknown): string {
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? "\n" + arg.stack : ""}`;
  }
  if (typeof arg === "string") return arg;
  if (typeof arg === "number" || typeof arg === "boolean") return String(arg);
  if (arg === null) return "null";
  if (arg === undefined) return "undefined";
  try {
    return JSON.stringify(arg);
  } catch {
    // Circular refs or unserialisable values — fall back to toString.
    try { return String(arg); } catch { return "[unserialisable]"; }
  }
}

function _push(level: ConsoleLevel, args: unknown[]) {
  const message = args.map(_safeStringify).join(" ").slice(0, MAX_MESSAGE_CHARS);
  _buffer.push({ level, message, at: new Date().toISOString() });
  if (_buffer.length > MAX_ENTRIES) _buffer.shift();
}

let _installed = false;

/**
 * Patch console.{log,info,warn,error,debug} so each call also lands in
 * our ring buffer. Idempotent — multiple imports / HMR reloads only
 * install once. Original behaviour is preserved exactly.
 */
export function installConsoleCapture(): void {
  if (_installed) return;
  if (typeof window === "undefined" || typeof console === "undefined") return;
  _installed = true;

  const levels: ConsoleLevel[] = ["log", "info", "warn", "error", "debug"];
  const cons = console as unknown as Record<string, (...args: unknown[]) => void>;
  for (const level of levels) {
    const orig = cons[level]?.bind(console);
    if (typeof orig !== "function") continue;
    cons[level] = (...args: unknown[]) => {
      try { _push(level, args); } catch { /* swallow */ }
      orig(...args);
    };
  }

  // Also capture window.onerror + unhandledrejection so JS exceptions
  // that never reach console.error still show up in the report.
  window.addEventListener("error", (e) => {
    try {
      _push("error", [
        `[window.onerror] ${e.message}`,
        e.error?.stack ? `\n${e.error.stack}` : "",
      ]);
    } catch { /* noop */ }
  });
  window.addEventListener("unhandledrejection", (e) => {
    try {
      _push("error", [
        "[unhandledrejection]",
        e.reason instanceof Error ? `${e.reason.message}\n${e.reason.stack}` : e.reason,
      ]);
    } catch { /* noop */ }
  });
}

/**
 * Snapshot the current console buffer. Returns a defensive copy so the
 * caller can serialise without worrying about further mutations.
 */
export function getConsoleSnapshot(): ConsoleEntry[] {
  return _buffer.slice();
}

/** Test helper — wipe the buffer (used by jest specs). */
export function _clearConsoleBuffer(): void {
  _buffer.length = 0;
}

// ──────────────────────────────────────────────────────────────────────
// Telemetry snapshot
// ──────────────────────────────────────────────────────────────────────

interface ConnectionLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NavigatorWithExtras extends Navigator {
  connection?: ConnectionLike;
  deviceMemory?: number;
}

/**
 * Snapshot the browser's environment. Cheap (sub-ms), no I/O. Safe to
 * call on every turn — we do it on first chat AND at submit so the
 * report reflects the page the user was on when they hit "send".
 */
export function collectTelemetry(): Telemetry {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    // SSR / non-browser — return a minimal shape.
    return {
      user_agent: "",
      platform: "",
      language: "",
      languages: [],
      timezone: "",
      online: false,
      cookie_enabled: false,
      hardware_concurrency: null,
      device_memory: null,
      viewport: { width: 0, height: 0, pixel_ratio: 1 },
      screen: { width: 0, height: 0 },
      page: { url: "", pathname: "", search: "", referrer: "" },
      collected_at: new Date().toISOString(),
    };
  }
  const nav = navigator as NavigatorWithExtras;
  const conn = nav.connection;
  return {
    user_agent: nav.userAgent || "",
    platform: nav.platform || "",
    language: nav.language || "",
    languages: Array.isArray(nav.languages) ? Array.from(nav.languages) : [],
    timezone: (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; }
      catch { return ""; }
    })(),
    online: typeof nav.onLine === "boolean" ? nav.onLine : true,
    cookie_enabled: typeof nav.cookieEnabled === "boolean" ? nav.cookieEnabled : true,
    hardware_concurrency: typeof nav.hardwareConcurrency === "number"
      ? nav.hardwareConcurrency : null,
    device_memory: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    viewport: {
      width: window.innerWidth || 0,
      height: window.innerHeight || 0,
      pixel_ratio: window.devicePixelRatio || 1,
    },
    screen: {
      width: window.screen?.width || 0,
      height: window.screen?.height || 0,
    },
    page: {
      url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      pathname: window.location.pathname || "",
      search: window.location.search || "",
      referrer: document.referrer || "",
    },
    connection: conn ? {
      effective_type: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
      save_data: conn.saveData,
    } : undefined,
    collected_at: new Date().toISOString(),
  };
}

// Self-install on module load so the side effect happens regardless of
// whether the importer remembers to call installConsoleCapture(). The
// guard inside the function keeps this idempotent.
installConsoleCapture();
